import { Link, notFound, useParams } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { productQuery } from '@/queries';
import { ArrowLeft, Package, Truck } from '@phosphor-icons/react';

import { BuyPanel } from '@/components/buy-panel';
import { GarmentMockup } from '@/components/garment-mockup';
import { ProductCard } from '@/components/product-card';
import { Badge } from '@/components/ui';
import { accentTextStyle } from '@/lib/accent';
import { exGst, formatCents } from '@/lib/money';

export default function ProductPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const { item, blank, method, config, catalogue } = useSuspenseQuery(productQuery(id)).data;
  if (!item) throw notFound();

  const sizes = blank?.sizes ?? ['One Size'];
  const inStock = item.fulfilment === 'pod' || (item.stock_qty ?? 0) > 0;
  const freeShipping = item.retail_price_cents >= config.free_shipping_threshold_cents;

  const related = catalogue
    .filter((c) => c.brand_id === item.brand_id && c.id !== item.id)
    .slice(0, 4);

  return (
    <div className="mx-auto max-w-[1180px] px-[30px] py-8">
      <Link
        to="/shop/$brand" params={{ brand: item.brand.slug }}
        className="inline-flex items-center gap-2 py-2 text-sm font-semibold text-muted hover:text-ink"
      >
        <ArrowLeft size={16} weight="bold" aria-hidden />
        {item.brand.name}
      </Link>

      <div className="mt-4 grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
        <div className="rounded-[10px] border border-line bg-panel p-6">
          <GarmentMockup
            type={item.type}
            colorHex={item.color_hex}
            designUrl={item.design_url}
            placement={{
              pos_x: item.pos_x ?? 50,
              pos_y: item.pos_y ?? 48,
              scale: item.scale ?? 60,
            }}
            label={`${item.title} in ${item.color_name}`}
            className="mx-auto aspect-square w-full max-w-[520px]"
          />
        </div>

        <div>
          <span
            className="accent-text text-[11px] font-extrabold uppercase tracking-[0.08em]"
            style={accentTextStyle(item.brand.accent_hex)}
          >
            {item.brand.name}
          </span>
          <h1 className="mt-1.5 text-[30px] font-extrabold leading-tight tracking-[-0.03em] sm:text-[36px]">
            {item.title}
          </h1>

          <p className="mt-4 text-[32px] font-extrabold tracking-[-0.03em]">
            {formatCents(item.retail_price_cents)}
          </p>
          {/* AC-1.4: GST is itemised, not implied. */}
          <p className="mt-1 text-sm text-muted">
            Includes {formatCents(item.retail_price_cents - exGst(item.retail_price_cents))} GST
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
            <Badge tone="neutral">{item.color_name}</Badge>
            {method && <Badge tone="neutral">{method.name}</Badge>}
            {item.fulfilment === 'stock' ? (
              <Badge tone={inStock ? 'ok' : 'warn'}>
                {inStock ? `${item.stock_qty} in stock` : 'Out of stock'}
              </Badge>
            ) : (
              <Badge tone="brand">Printed to order</Badge>
            )}
          </div>

          <hr className="my-7 border-line" />

          <BuyPanel publishedId={item.id} sizes={sizes} inStock={inStock} />

          <dl className="mt-8 space-y-3 border-t border-line pt-6 text-[15px]">
            <div className="flex gap-3">
              <dt className="sr-only">Fulfilment</dt>
              <Package size={19} weight="bold" className="mt-0.5 shrink-0 text-muted" aria-hidden />
              <dd className="text-muted">
                {item.fulfilment === 'stock'
                  ? 'Held in stock and dispatched within one business day.'
                  : `Printed to order. ${method?.turnaround ?? '1-2 weeks'} before dispatch.`}
              </dd>
            </div>
            <div className="flex gap-3">
              <dt className="sr-only">Shipping</dt>
              <Truck size={19} weight="bold" className="mt-0.5 shrink-0 text-muted" aria-hidden />
              <dd className="text-muted">
                {freeShipping
                  ? 'Free shipping on this item.'
                  // "over" is wrong by one cent. Both this line and checkout
                  // use `>= threshold`, so an order of exactly $150.00 ships
                  // free while these words say it has to beat $150.00. R-381.
                  : `Flat-rate shipping ${formatCents(config.shipping_flat_cents)}, free from ${formatCents(config.free_shipping_threshold_cents)}.`}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-20">
          <h2 className="mb-6 text-2xl font-bold tracking-[-0.02em]">
            More from {item.brand.name}
          </h2>
          <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
            {related.map((r) => (
              <ProductCard key={r.id} item={r} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
